import type React from "react";
import { useState } from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
  cn,
} from "@fluid-app/ui-primitives";

type ComboboxOption = {
  label: string;
  value: string;
};

type ComboboxProps = {
  /** Available options to choose from. */
  options: ReadonlyArray<ComboboxOption>;
  /** Currently selected value (controlled). */
  value: string;
  /** Called when an option is selected or cleared. */
  onValueChange: (value: string) => void;
  /** Placeholder shown when no option is selected. */
  placeholder?: string;
  /** Message shown when the search query matches no options. */
  emptyText?: string;
  /** Disables the input. */
  disabled?: boolean;
};

export type { ComboboxOption, ComboboxProps };

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  emptyText = "No results found.",
  disabled = false,
}: ComboboxProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedOption = options.find((opt) => opt.value === value);

  const filtered = search
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  return (
    <Command className="overflow-visible bg-transparent">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverAnchor asChild>
          <div
            className={cn(
              "border-input flex h-9 w-full cursor-pointer items-center rounded-md border bg-transparent shadow-xs transition-[color,box-shadow]",
              open && "border-ring ring-ring/50 ring-[3px]",
              disabled && "cursor-not-allowed opacity-50",
            )}
            onClick={() => {
              if (!disabled) setOpen(true);
            }}
          >
            <input
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              placeholder={selectedOption ? selectedOption.label : placeholder}
              value={open ? search : ""}
              className={cn(
                "placeholder:text-foreground h-full w-full cursor-text rounded-md bg-transparent px-3 text-sm outline-none disabled:cursor-not-allowed",
                !open && !selectedOption && "placeholder:text-muted-foreground",
              )}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!open) setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setOpen(false);
                  setSearch("");
                }
              }}
            />
            <ChevronDownIcon className="mr-2 size-4 shrink-0 opacity-50" />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="bg-popover w-[var(--radix-popover-trigger-width)] rounded-md border p-0 shadow-md"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <CommandList>
            {filtered.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onValueChange(opt.value === value ? "" : opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {opt.label}
                  <CheckIcon
                    className={cn(
                      "ml-auto size-4",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}
